import { Button, buttonStyles, cn } from '@skupreme/shared/ui';
import { AppTable, AppTableCell, AppTableRow } from '@components/AppTable';
import {
  ShippingTypesIcons,
  ShipmentTypeKeys,
  WhiteShippingTypesIcons,
  ShipmentStatus,
} from '@utility/constants/common';
import { useMemo } from 'react';
import { Link } from 'react-router';
import { Flag } from '@elements/Flag';
import { useSidebar } from '@contexts/sidebar';
import { Permissions } from '@utility/constants/permission';
import { MuteText } from '@elements/Common';
import { StatusStepper } from '@page-components/shipment/list/row';
import { ItemAllocation } from '../Allocation';
import { CreateShipmentButton } from '../CreateShipmentButton';
import { shipmentItemColumn } from '../common';

const CELL = 'sk:px-2.5! sk:py-2!';

/* The table's own frame is dropped: it sits inside the tab's card. */
const SHIPMENT_TABLE = cn(
  'sk:mt-1 sk:max-h-[21vh] sk:overflow-y-auto',
  'sk:[&_th]:border-b-0! sk:[&_th]:pl-0! sk:[&_td]:pl-0!',
  'sk:[&_.status-column]:w-25! sk:[&_.type-column]:w-12.5!',
);

type PlannedShipment = {
  _id?: string;
  display_id?: string;
  shipping_type?: string;
  destination?: { _id?: string; name?: string; region_name?: string };
};

/** A destination with the dot that ties it to its colour in the allocation bar. */
function Destination({
  item,
  legendColor,
}: {
  item?: PlannedShipment;
  legendColor?: string;
}) {
  return (
    <div className="sk:flex sk:min-w-0 sk:items-center sk:gap-1.5 sk:whitespace-nowrap">
      <span
        aria-hidden
        className="sk:size-1.5 sk:shrink-0 sk:rounded-full"
        style={{ background: legendColor || 'transparent' }}
      />
      <Flag className="sk:mr-2" name={item?.destination?.region_name || 'US'} />
      <div className="sk:min-w-0">
        <div className="sk:truncate">{item?.destination?.name || '-'}</div>
        <MuteText>{item?.display_id}</MuteText>
      </div>
    </div>
  );
}

function ShippingType({ type, white }: { type?: string; white?: boolean }) {
  const icons = white ? WhiteShippingTypesIcons : ShippingTypesIcons;
  return (
    <div className="sk:flex sk:items-center sk:gap-2 sk:whitespace-nowrap">
      {type ? <img alt="" src={icons[type]} /> : null}
      <span>{type}</span>
    </div>
  );
}

function createShipmentUrl(
  item: PlannedShipment | undefined,
  origin_id: string,
  wholesaleView?: boolean,
) {
  return wholesaleView
    ? `/app/scm/shipments/create?type=${encodeURIComponent(
        ShipmentTypeKeys.WS,
      )}&shipping=${item?.shipping_type}&origin_id=${origin_id}&step=1`
    : `/app/scm/shipments/create?type=${encodeURIComponent(
        ShipmentTypeKeys.PO,
      )}&destination=${item?.destination?._id}&shipping=${
        item?.shipping_type
      }&origin_id=${origin_id}&step=1`;
}

const LINK_BUTTON = buttonStyles({
  variant: 'primary',
  size: 'sm',
  className: 'sk:no-underline',
});

export function POShipmentItemRow({
  item,
  origin_id,
  allocationData,
  fromItemAllocation,
  shipmentAllowed,
  wholesaleView,
}) {
  const destination = useMemo(
    () => allocationData.find(x => item?.destination?._id === x.key),
    [item, allocationData],
  );
  const status = useMemo(() => {
    if (item.status === ShipmentStatus.shipped) {
      return ShipmentStatus.in_transit;
    }
    return item.status;
  }, [item]);

  return (
    <AppTableRow>
      <AppTableCell className={CELL}>
        <Destination item={item} legendColor={destination?.color} />
      </AppTableCell>

      {item._id ? (
        <AppTableCell className={cn(CELL, 'status-cell')}>
          {/* @ts-expect-error - TS2741: Property 'fallBackElement' is missing in type '{ status: any... */}
          <StatusStepper status={status} customClass="sk:mb-0" />
        </AppTableCell>
      ) : (
        <AppTableCell className={CELL}>-</AppTableCell>
      )}
      <AppTableCell className={CELL}>
        <ShippingType type={item?.shipping_type} white={fromItemAllocation} />
      </AppTableCell>
      <AppTableCell className={CELL} textAlign="right">
        {item._id ? (
          <Link to={`/app/scm/shipments/${item._id}`} className={LINK_BUTTON}>
            View Shipment
          </Link>
        ) : (
          shipmentAllowed && (
            <Link
              to={createShipmentUrl(item, origin_id, wholesaleView)}
              className={LINK_BUTTON}
            >
              Create
            </Link>
          )
        )}
      </AppTableCell>
    </AppTableRow>
  );
}

function PredictedShipmentRow({
  item,
  origin_id,
  allocationData,
  fromItemAllocation,
  shipmentAllowed,
  wholesaleView,
}) {
  const destination = useMemo(
    () =>
      allocationData.find(
        x =>
          item?.destination?._id === x.key ||
          (x.key === 'unallocated' && item.status === 'prediction'),
      ),
    [item, allocationData],
  );

  return (
    <div className="sk:grid sk:grid-cols-[2fr_1fr_1fr] sk:items-center sk:gap-2 sk:border-0 sk:border-b sk:border-solid sk:border-rule sk:bg-danger-bg sk:px-2.5 sk:py-1.5 sk:last:border-b-0">
      <Destination item={item} legendColor={destination?.color} />
      <ShippingType type={item?.shipping_type} white={fromItemAllocation} />
      <div className="sk:text-right">
        {shipmentAllowed ? (
          <Link
            to={createShipmentUrl(item, origin_id, wholesaleView)}
            className={LINK_BUTTON}
          >
            Create
          </Link>
        ) : (
          <Button variant="primary" size="sm" isDisabled>
            Create
          </Button>
        )}
      </div>
    </div>
  );
}

export function ShipmentsTab({
  supplierId,
  shipments,
  allowShipments,
  allocationData,
  wholesaleView,
}) {
  /* @ts-expect-error - TS2339: Property 'getMenuByUrl' does not exist on type 'unknown'. */
  const { getMenuByUrl } = useSidebar();

  const shipmentAllowed = useMemo(() => {
    const permission = getMenuByUrl('/app/scm/shipments')?.permission;
    return (
      allowShipments &&
      ![Permissions.user, Permissions.hidden].includes(permission)
    );
  }, [allowShipments]);

  const { created, predicted, predetermined } = useMemo(() => {
    const created = [];
    const predicted = [];
    const predetermined = [];
    (shipments || []).forEach(item => {
      if (item.status === 'prediction') {
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        predicted.push(item);
      } else if (item.status === 'predetermined') {
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        predetermined.push(item);
      } else {
        // @ts-expect-error - TS2345: Argument of type 'any' is not assignable to parameter of type 'never'.
        created.push(item);
      }
    });
    return { created, predicted, predetermined };
  }, [shipments]);

  return (
    <>
      {(allocationData || []).length > 0 && (
        <ItemAllocation
          allocationData={allocationData}
          shipments={(shipments || []).filter(x => x.status === 'prediction')}
          supplierId={supplierId}
        />
      )}
      <div>
        <div className="sk:mt-3">
          <MuteText>Created Shipments</MuteText>
        </div>
        <AppTable
          RowComponent={
            // @ts-expect-error - TS2739: Type '{ origin_id: any; allocationData: any; shipmentAllowed: any; wholesaleView...
            <POShipmentItemRow
              origin_id={supplierId}
              allocationData={allocationData || []}
              shipmentAllowed={shipmentAllowed}
              wholesaleView={wholesaleView}
            />
          }
          fixed
          data={created}
          // @ts-expect-error - TS2322: Type '({ text: string; key: string; width: number; textAlign: string; className?...
          columns={shipmentItemColumn}
          noShadow
          // @ts-expect-error - TS2322: Type '"start"' is not assignable to type '"center" | "left" | "right" | undefine...
          textAlign="start"
          noDataMessage={<p className="sk:ml-4">No shipment yet!</p>}
          headerOffSet={0}
          wrapperClass={SHIPMENT_TABLE}
        />
        {predetermined.length > 0 && (
          <>
            <div className="sk:mt-3">
              <MuteText>Pre-determined Shipments</MuteText>
            </div>
            {predetermined.map((item, index) => (
              // @ts-expect-error - TS2739: Type '{ key: number; item: never; origin_id: any; allocationData: any; shipmentA...
              <PredictedShipmentRow
                key={index}
                item={item}
                origin_id={supplierId}
                allocationData={allocationData || []}
                shipmentAllowed={shipmentAllowed}
              />
            ))}
          </>
        )}
        {shipmentAllowed && (
          <>
            {predicted.length > 0 && (
              <>
                <div className="sk:mt-3">
                  <MuteText>Predicted Shipments</MuteText>
                </div>
                {predicted.map((item, index) => (
                  // @ts-expect-error - TS2741: Property 'fromItemAllocation' is missing in type '{ key: number; item: never; or...
                  <PredictedShipmentRow
                    key={index}
                    item={item}
                    origin_id={supplierId}
                    allocationData={allocationData || []}
                    shipmentAllowed={shipmentAllowed}
                    wholesaleView={wholesaleView}
                  />
                ))}
              </>
            )}
            <CreateShipmentButton
              wholesaleView={wholesaleView}
              supplierId={supplierId}
            />
          </>
        )}
      </div>
    </>
  );
}
